// GitHub icon button that sits in the Project Files header of BuilderEditor.
//
// On click, fetches the app's git status and opens a menu mirroring the
// per-app grid menu: Connect / Sync now / Pull / Unlink, plus a read-only
// summary row when linked.
//
// Self-contained: owns its own status, snackbar, and link-dialog state so the
// editor file doesn't need to grow more wires.

import React, { useCallback, useState } from "react";
import {
  Alert,
  Divider,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import LucideIcon from "../../../components/LucideIcon";
import type { BuilderApiClient } from "../api";
import type { GitSyncStatus } from "../types";
import GitLinkDialog from "./GitLinkDialog";

interface Props {
  api: BuilderApiClient;
  appId: string;
  companyId: string;
  getToken: () => Promise<string>;
  openDialog: (title: string, content: string, buttonText: string, onClick: () => void) => void;
}

const GitFilesMenu: React.FC<Props> = ({ api, appId, companyId, getToken, openDialog }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [status, setStatus] = useState<GitSyncStatus | null>(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [busy, setBusy] = useState<"push" | "pull" | "unlink" | null>(null);
  const [snack, setSnack] = useState<
    { msg: string; severity: "success" | "error" | "info" } | null
  >(null);

  const refreshStatus = useCallback(async () => {
    try {
      const s = await api.getGitStatus(appId);
      setStatus(s);
    } catch {
      setStatus({ linked: false, syncInProgress: false, drift: 0 });
    }
  }, [api, appId]);

  const open = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      setAnchor(e.currentTarget);
      setStatus(null);
      void refreshStatus();
    },
    [refreshStatus],
  );

  const close = useCallback(() => {
    setAnchor(null);
    setStatus(null);
  }, []);

  const runAction = useCallback(
    async (kind: "push" | "pull" | "unlink") => {
      setBusy(kind);
      try {
        if (kind === "push") {
          await api.pushToGit(appId);
          setSnack({ msg: "Sync started — pushing local changes to GitHub", severity: "info" });
        } else if (kind === "pull") {
          await api.pullFromGit(appId);
          setSnack({ msg: "Pull started — fetching changes from GitHub", severity: "info" });
        } else {
          await api.unlinkGitRepo(appId);
          setSnack({ msg: "Unlinked from GitHub", severity: "success" });
        }
      } catch (e: any) {
        setSnack({
          msg: e?.response?.data?.error || e?.message || `${kind} failed`,
          severity: "error",
        });
      } finally {
        setBusy(null);
      }
    },
    [api, appId],
  );

  return (
    <>
      <Tooltip title="GitHub sync">
        <IconButton size="small" onClick={open}>
          <LucideIcon name="Github" size={18} />
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchor}
        open={!!anchor}
        onClose={close}
        slotProps={{ paper: { sx: { minWidth: 280, maxWidth: 360 } } }}
      >
        {!status && <MenuItem disabled>Loading GitHub status…</MenuItem>}

        {status && !status.linked && (
          <MenuItem
            onClick={() => {
              close();
              setLinkOpen(true);
            }}
          >
            <ListItemText
              primary="Connect GitHub"
              secondary="Link this app to a repo"
            />
          </MenuItem>
        )}

        {status?.linked && (
          <MenuItem disabled sx={{ opacity: 1, "&.Mui-disabled": { opacity: 1 } }}>
            <Stack sx={{ width: "100%" }}>
              <Typography variant="caption" color="text.secondary">
                GitHub
              </Typography>
              <Typography variant="body2" noWrap>
                {status.owner}/{status.repo}
                <Typography component="span" color="text.secondary"> · {status.branch}</Typography>
              </Typography>
              {status.drift > 0 && (
                <Typography variant="caption" color="primary">
                  {status.drift} local edit{status.drift === 1 ? "" : "s"} since last sync
                </Typography>
              )}
              {status.pendingPrUrl && (
                <Typography
                  variant="caption"
                  component="a"
                  href={status.pendingPrUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ color: "warning.main" }}
                  onClick={(e) => e.stopPropagation()}
                >
                  Conflicts opened a PR — open and merge
                </Typography>
              )}
            </Stack>
          </MenuItem>
        )}

        {status?.linked && (
          <MenuItem
            disabled={busy !== null || status.syncInProgress}
            onClick={() => {
              close();
              void runAction("push");
            }}
          >
            Sync now (push to GitHub)
          </MenuItem>
        )}
        {status?.linked && (
          <MenuItem
            disabled={busy !== null || status.syncInProgress}
            onClick={() => {
              close();
              void runAction("pull");
            }}
          >
            Pull from GitHub
          </MenuItem>
        )}
        {status?.linked && <Divider />}
        {status?.linked && (
          <MenuItem
            disabled={busy !== null}
            onClick={() => {
              close();
              openDialog(
                "Unlink GitHub repo?",
                `This disconnects "${status.owner}/${status.repo}" from this app. The GitHub repo itself isn't touched.`,
                "Unlink",
                () => void runAction("unlink"),
              );
            }}
          >
            Unlink GitHub
          </MenuItem>
        )}
      </Menu>

      <GitLinkDialog
        open={linkOpen}
        onClose={() => setLinkOpen(false)}
        api={api}
        appId={appId}
        companyId={companyId}
        getToken={getToken}
        onLinked={(s) => {
          setStatus(s);
          setLinkOpen(false);
        }}
      />

      <Snackbar
        open={!!snack}
        autoHideDuration={6000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {snack ? (
          <Alert severity={snack.severity} onClose={() => setSnack(null)}>
            {snack.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </>
  );
};

export default GitFilesMenu;
